import { createApi } from "@reduxjs/toolkit/query/react";
import { baseURL } from "data/api/v1/endpoints";
import { baseQueryWithRedirect } from "features/api/utils/apiUtils";
import type { ActiveMessages, Message, MessageOptions, MessagePage } from "./types";

export const messagesApi = createApi({
	reducerPath: "messagesApi",
	baseQuery: baseQueryWithRedirect(baseURL.replace(/\/$/, "") + "/api/v1", false),
	tagTypes: ["Messages"],
	endpoints: (builder) => ({
		getMessages: builder.query<Message[], "current" | "expired">({
			query: (status) => ({ url: "/messages", params: { status } }),
			transformResponse: (response: { data: Message[] }) => response.data,
			providesTags: ["Messages"]
		}),
		getMessage: builder.query<Message, string>({
			query: (id) => "/messages/" + encodeURIComponent(id),
			transformResponse: (response: { data: Message }) => response.data,
			providesTags: ["Messages"]
		}),
		getMessageOptions: builder.query<MessageOptions, void>({
			query: () => ({ url: "/messages/options", timeout: 30000 }),
			transformResponse: (response: { data: MessageOptions }) => response.data
		}),
		saveMessage: builder.mutation<Message, Message>({
			query: (message) => ({
				url: "/messages" + (message.messageId ? "/" + encodeURIComponent(message.messageId) : ""),
				method: message.messageId ? "PUT" : "POST",
				body: message
			}),
			transformResponse: (response: { data: Message }) => response.data,
			invalidatesTags: ["Messages"]
		}),
		deleteMessage: builder.mutation<void, string>({
			query: (id) => ({ url: "/messages/" + encodeURIComponent(id), method: "DELETE" }),
			invalidatesTags: ["Messages"]
		}),
		getActiveMessages: builder.query<ActiveMessages, { page: MessagePage; contextKey: string }>({
			query: ({ page }) => ({ url: "/messages/active", params: { page } }),
			transformResponse: (response: { data: ActiveMessages }) => response.data,
			providesTags: ["Messages"]
		}),
		getOrderStatus: builder.query<
			{ canSubmit: boolean; message?: string; nextChangeUtc?: string },
			{ accountId: string; productLineId: string; contextKey: string }
		>({
			query: ({ accountId, productLineId }) => ({
				url: "/messages/order-status",
				params: { accountId, productLineId }
			}),
			transformResponse: (response: { data: { canSubmit: boolean; message?: string; nextChangeUtc?: string } }) =>
				response.data,
			providesTags: ["Messages"]
		})
	})
});
export const {
	useGetMessagesQuery,
	useGetMessageQuery,
	useGetMessageOptionsQuery,
	useSaveMessageMutation,
	useDeleteMessageMutation,
	useGetActiveMessagesQuery,
	useGetOrderStatusQuery
} = messagesApi;
